<template>
  <div class="flex flex-col items-center min-h-[50vh]">
    <SearchErrorMessage :response="response" />
    <div class="relative flex flex-col w-full">
      <GoogleMap
        v-if="MAPS_API_KEY"
        ref="mapRef"
        map-id="fc0959b7dc4da895"
        :api-key="MAPS_API_KEY"
        :center="usCenterCoordinates"
        :zoom="5"
        :zoom-control="true"
        :clickable-icons="false"
        :gesture-handling="!$breakpoints.lg.value ? 'greedy' : 'auto'"
        class="h-[50vh]"
        disable-default-ui
      >
        <HomeMarker
          v-if="currentLocation"
          :coordinates="currentLocation"
        />
        <StoreMarker
          v-for="address in addresses"
          :key="address.id"
          :address="address"
        />
        <StoreInfoWindow
          v-if="mapInteraction.active"
          :address="mapInteraction.active"
        />
      </GoogleMap>
      <div class="w-full flex flex-col sm:rounded-lg lg:absolute lg:w-[430px] top-5 left-7 max-h-[calc(50vh-1.25rem)] bg-white lg:shadow-md">
        <SearchField
          v-model="inputText"
          @reset="store.reset"
          @submit="handleSubmit"
        />
        <StoreList
          v-bind="{ addresses, pagination, loading, mapRef }"
          @paginate="search"
        />
      </div>
    </div>
    <LocatorFooter v-if="showFooter" />
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAmplitude, EventTypes } from '@/services/amplitude'
import { useRetailStore } from '@/stores/retail'
import { mapInteraction } from '@/services/maps'

import { MAPS_API_KEY, usCenterCoordinates } from '@/services/maps'

import { GoogleMap } from 'vue3-google-map'

import SearchErrorMessage from './search-error-message.vue'
import SearchField from './search-field.vue'
import StoreList from './store-list.vue'
import StoreMarker from './store-marker.vue'
import StoreInfoWindow from './store-info-window.vue'
import HomeMarker from './home-marker.vue'
import LocatorFooter from './locator-footer.vue'

const amplitude = useAmplitude()
const store = useRetailStore()

const { response, addresses, pagination } = storeToRefs(store)

export interface Props {
  searchStore?: string
  showFooter?: boolean
}

const props = defineProps<Props>()

const mapRef = ref()
const loading = ref(false)
const query = ref('')
const inputText = ref('')
const currentLocation = ref<{ latitude: number, longitude: number }>()

const handleSubmit = () => {
  store.reset()
  query.value = inputText.value
  search()
}

const search = async (page: number = 1) => {
  loading.value = true
  const { value } = query
  if (value.length === 0) {
    return
  } else if (value === 'Current Location') {
    return await trySearchUserPosition()
  }
  amplitude.track(EventTypes.RETAIL_SEARCH, {
    'Query': value
  })
  await store.search(value, page, props.searchStore)
  fitMapToAddresses()
  loading.value = false
}

const fitMapToAddresses = () => {
  if (mapRef.value?.ready && addresses.value?.length) {

    const bounds = new window.google.maps.LatLngBounds()
    for (let address of addresses.value) {
      const { lat, lng } = address
      if (lat && lng) {
        bounds.extend({ lat, lng })
      }
    }
    const left = window.innerWidth < 1024 ? 0 : 430
    mapRef.value.map.fitBounds(bounds, { left, top: 40, bottom: 20, right: 20 })
  }
}

const trySearchUserPosition = async () => {
  navigator.geolocation.getCurrentPosition(async (pos) => {
    currentLocation.value = pos.coords
    query.value = `${currentLocation.value.latitude}, ${currentLocation.value.longitude}`
    await search()
  }, (error) => {
    console.log('error?', error)
  }, {
    maximumAge: 3600000
  })
}

trySearchUserPosition()
</script>
